// Tab 3: Furnizori CRUD.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Truck, Mail, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

import {
  listSuppliers, createSupplier, updateSupplier, deleteSupplier,
} from "@/lib/inventory";
import { fmtDate } from "@/lib/format";

export default function SuppliersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // supplier or "new"

  const reload = useCallback(async () => {
    setLoading(true);
    try { setItems(await listSuppliers()); }
    catch { toast.error("Eroare"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const remove = async (id) => {
    if (!window.confirm("Ștergi acest furnizor?")) return;
    try {
      await deleteSupplier(id);
      toast.success("Furnizor șters");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    }
  };

  return (
    <div className="space-y-4" data-testid="suppliers-tab">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">{items.length} furnizori</div>
        <Button size="sm" onClick={() => setEditing({})} data-testid="supplier-new-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Adaugă furnizor
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <Truck className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <div className="text-sm">Niciun furnizor încă.</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((s) => (
            <Card key={s.id} className="p-4 group hover:shadow-md transition" data-testid={`supplier-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-semibold text-base truncate">{s.name}</div>
                  {s.contact_person && (
                    <div className="text-xs text-muted-foreground">{s.contact_person}</div>
                  )}
                </div>
                <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition">
                  <button onClick={() => setEditing(s)} className="text-muted-foreground hover:text-foreground" data-testid={`supplier-edit-${s.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => remove(s.id)} className="text-muted-foreground hover:text-red-500" data-testid={`supplier-del-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-xs">
                {s.phone && <div className="flex items-center gap-2"><Phone className="h-3 w-3 text-muted-foreground" />{s.phone}</div>}
                {s.email && <div className="flex items-center gap-2"><Mail className="h-3 w-3 text-muted-foreground" />{s.email}</div>}
                {s.address && <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-3 w-3" />{s.address}</div>}
              </div>
              <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>{s.items_count} SKU asignate</span>
                {s.lead_time_days != null && <span>Lead time: {s.lead_time_days}z</span>}
                {s.last_received_at && <span>Ultima recepție: {fmtDate(s.last_received_at)}</span>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <SupplierDialog
        editing={editing}
        onClose={() => setEditing(null)}
        onDone={async () => { setEditing(null); await reload(); }}
      />
    </div>
  );
}


function SupplierDialog({ editing, onClose, onDone }) {
  const open = !!editing;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || "",
        contact_person: editing.contact_person || "",
        phone: editing.phone || "",
        email: editing.email || "",
        address: editing.address || "",
        lead_time_days: editing.lead_time_days ?? "",
        notes: editing.notes || "",
      });
    }
  }, [editing]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Numele este obligatoriu");
    setSaving(true);
    try {
      const payload = {
        ...form,
        lead_time_days: form.lead_time_days === "" ? null : parseInt(form.lead_time_days, 10),
      };
      if (editing?.id) await updateSupplier(editing.id, payload);
      else await createSupplier(payload);
      toast.success("Furnizor salvat");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="supplier-dialog">
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Editează furnizor" : "Furnizor nou"}</DialogTitle>
          <DialogDescription>Date contact + termene de livrare pentru aprovizionare.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Nume *">
            <Input value={form.name || ""} onChange={(e) => setF("name", e.target.value)} data-testid="supplier-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Persoană contact">
              <Input value={form.contact_person || ""} onChange={(e) => setF("contact_person", e.target.value)} />
            </Field>
            <Field label="Telefon">
              <Input value={form.phone || ""} onChange={(e) => setF("phone", e.target.value)} data-testid="supplier-phone" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email">
              <Input value={form.email || ""} onChange={(e) => setF("email", e.target.value)} data-testid="supplier-email" />
            </Field>
            <Field label="Termen livrare (zile)">
              <Input type="number" value={form.lead_time_days ?? ""} onChange={(e) => setF("lead_time_days", e.target.value)} />
            </Field>
          </div>
          <Field label="Adresă">
            <Input value={form.address || ""} onChange={(e) => setF("address", e.target.value)} />
          </Field>
          <Field label="Observații">
            <Textarea value={form.notes || ""} onChange={(e) => setF("notes", e.target.value)} rows={3} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} disabled={saving} data-testid="supplier-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Salvează
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">{label}</div>
      {children}
    </div>
  );
}
